import StatisticalCard, {
    StatisticalCardProps,
} from '@/components/CardStatistical'
import CheckCircleIcons from '@/icons/CheckCircleIcons'
import ContractNewIcons from '@/icons/ContractNewIcons'
import ContractPendingIcons from '@/icons/ContractPendingIcons'
import CoolleadIcons from '@/icons/CoolleadIcons'
import ErrorIcons from '@/icons/ErrorIcons'
import ReceiptIcons from '@/icons/ReceiptIcons'
import { getContractCustomersStatistical } from '@/modules/Contract/services/customer'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { t } from 'i18next'
import ActionLog from './ActionLog'
import Chart from './Chart'
import useDebounce from '@/hooks/useDebounce'
import { CUSTOMER_CONTRACT_TYPE_LABELS } from '@/modules/Contract/constants'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface StatisticalProps {
    filters?: string
}

const Statistical: React.FC<StatisticalProps> = ({ filters }) => {
    const debouncedFilters = useDebounce(filters, 500) // debounce 500ms

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.CONTRACTS_STATISTICAL, debouncedFilters],
            queryFn: () =>
                getContractCustomersStatistical({
                    filters: debouncedFilters,
                    q: '',
                }),
        },
        {
            showLoading: false,
            showError: false,
        }
    )

    const dataStatistical: Partial<StatisticalCardProps>[] = [
        {
            label: CUSTOMER_CONTRACT_TYPE_LABELS.Summary,
            className: '',
            type: 'highlighted',
            icon: <ReceiptIcons />,
            color: 'primary',
            value: data?.result.total_count,
            scale: data?.result?.delta_total_percent,
        },
        {
            label: CUSTOMER_CONTRACT_TYPE_LABELS.Created,
            icon: <ContractNewIcons />,
            color: 'warning',
            className: '!col-span-2',
            value: data?.result.created_count,
            scale: data?.result?.delta_created_percent,
            href: generatePathDatacentral(
                paths.customerContractOverview,
                {
                    type: 'new',
                },
                {
                    filters: filters,
                }
            ),
        },
        {
            label: CUSTOMER_CONTRACT_TYPE_LABELS.Exchanging,
            color: 'processing',
            icon: <ContractPendingIcons />,
            className: '!col-span-2',
            value: data?.result.exchanging_count,
            scale: data?.result?.delta_exchanging_percent,
            href: generatePathDatacentral(
                paths.customerContractOverview,
                {
                    type: 'exchanging',
                },
                {
                    filters: filters,
                }
            ),
        },
        {
            label: CUSTOMER_CONTRACT_TYPE_LABELS.Expiried,
            value: data?.result.expired_count,
            scale: data?.result?.delta_expired_percent,
            icon: <ErrorIcons />,
            color: 'error',
            className: '!col-span-2',
            href: generatePathDatacentral(
                paths.customerContractOverview,
                {
                    type: 'expiried',
                },
                {
                    filters: filters,
                }
            ),
        },
        {
            label: CUSTOMER_CONTRACT_TYPE_LABELS.Uploading,
            icon: <CoolleadIcons />,
            color: 'lead',
            value: data?.result.uploading_count,
            scale: data?.result?.delta_uploading_percent,
            href: generatePathDatacentral(paths.customerContractOverview, {
                type: 'uploading',
            }),
        },
        {
            label: CUSTOMER_CONTRACT_TYPE_LABELS.Completed,
            value: data?.result.active_count,
            scale: data?.result?.delta_active_percent,
            color: 'success',
            icon: <CheckCircleIcons />,
            href: generatePathDatacentral(
                paths.customerContractOverview,
                {
                    type: 'completed',
                },
                {
                    filters: filters,
                }
            ),
        },
    ]
    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="mt-2">
            <div className="grid xs:grid-cols-1 md:grid-cols-2 gap-2">
                <div className="grid md:grid-cols-6 xs:grid-cols-2 gap-2">
                    {dataStatistical?.map((i, index) => {
                        return (
                            <StatisticalCard
                                key={index}
                                {...i}
                                label={t(i.label || '')}
                            />
                        )
                    })}
                </div>
                <div className="grid md:grid-cols-2 xs:grid-cols-1 gap-2">
                    <Chart data={data?.result} />
                    <ActionLog />
                </div>
            </div>
        </div>
    )
}

export default Statistical
